import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Connects to data-controller="file-drop"
// Drag-and-drop file upload for documents. Fully reusable — works for any
// documentable parent by accepting configurable values.
//
// Usage:
//   <div data-controller="file-drop"
//        data-file-drop-url-value="/customers/1/documents"
//        data-file-drop-frame-id-value="documents_list"
//        data-action="dragenter->file-drop#dragEnter
//                     dragover->file-drop#dragOver
//                     dragleave->file-drop#dragLeave
//                     drop->file-drop#drop">
//     <div data-file-drop-target="overlay" class="hidden ...">
//       Drop files to upload
//     </div>
//     ...existing content...
//   </div>
export default class extends Controller {
  static targets = ["overlay", "fileInput"]

  static values = {
    url: String,
    frameId: { type: String, default: "documents_list" },
    paramName: { type: String, default: "document" },
    fileField: { type: String, default: "file" }
  }

  connect() {
    this.dragCounter = 0
  }

  dragEnter(event) {
    event.preventDefault()
    if (!this.isFileDrag(event)) return

    this.dragCounter++
    if (this.dragCounter === 1) {
      this.showOverlay()
    }
  }

  dragOver(event) {
    if (!this.isFileDrag(event)) return
    event.preventDefault()
  }

  dragLeave(event) {
    event.preventDefault()
    if (!this.isFileDrag(event)) return

    this.dragCounter--
    if (this.dragCounter === 0) {
      this.hideOverlay()
    }
  }

  drop(event) {
    if (!this.isFileDrag(event)) return
    event.preventDefault()
    this.dragCounter = 0
    this.hideOverlay()

    const files = Array.from(event.dataTransfer.files)
    if (files.length === 0) return

    this.uploadFiles(files)
  }

  isFileDrag(event) {
    return event.dataTransfer?.types?.includes("Files")
  }

  browse() {
    this.fileInputTarget.click()
  }

  fileSelected() {
    const files = Array.from(this.fileInputTarget.files)
    if (files.length === 0) return

    this.uploadFiles(files)
    this.fileInputTarget.value = ""
  }

  async uploadFiles(files) {
    const total = files.length
    let completed = 0
    let failed = 0

    this.showProgress(completed, total)

    const uploads = files.map(async (file) => {
      try {
        await this.uploadFile(file)
      } catch {
        failed++
      } finally {
        completed++
        this.showProgress(completed, total)
      }
    })

    await Promise.all(uploads)
    this.hideOverlay()

    const succeeded = total - failed

    if (succeeded > 0) {
      this.reloadFrame()
      this.showToast("success", `${succeeded} document${succeeded === 1 ? "" : "s"} uploaded`)
    }

    if (failed > 0) {
      this.showToast("error", `${failed} document${failed === 1 ? "" : "s"} failed to upload`)
    }
  }

  async uploadFile(file) {
    // Direct-upload the file to storage, then send just the signed_id
    const signedId = await this.directUpload(file)

    const response = await fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      body: JSON.stringify({
        [this.paramNameValue]: { [this.fileFieldValue]: signedId }
      })
    })

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`)
    }

    return response.json()
  }

  directUpload(file) {
    const url = "/rails/active_storage/direct_uploads"
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, url)
      upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          resolve(blob.signed_id)
        }
      })
    })
  }

  showOverlay() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.remove("hidden")
  }

  hideOverlay() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.add("hidden")
  }

  showProgress(completed, total) {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.remove("hidden")
    const label = this.overlayTarget.querySelector("[data-role='label']")
    if (label) {
      label.textContent = (completed < total)
        ? `Uploading ${completed + 1} of ${total}...`
        : "Processing..."
    }
  }

  reloadFrame() {
    const frame = document.getElementById(this.frameIdValue)
    if (frame?.reload) {
      frame.reload()
    }
  }

  showToast(type, message) {
    document.dispatchEvent(new CustomEvent("toast:show", {
      detail: { type, message }
    }))
  }

  get csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta?.content || ""
  }
}
